import { useState } from 'react';
import { Ban, Mail, RotateCcw, Send, Users, X } from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/components/card';
import { Button } from '@/components/button';
import { InviteDialog } from '@/components/invite-dialog';
import { EmptyState } from '@/components/empty-state';
import { IconButton } from '@/components/icon-button';
import { Badge } from '@/components/badge';
import { EntityAvatar } from '@/components/entity-avatar';
import { Menu } from '@/components/menu';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import {
  useInvitations,
  useRevokeInvitation,
} from '@/lib/invitations';
import { useTeam, useSetTeamMemberAccess } from '@/lib/team';
import { formatDateLong, formatStatus } from '@/lib/format';
import type { TeamMemberData } from '@/types/api';

export function TeamTab() {
  const { data, isLoading } = useInvitations();
  const revoke = useRevokeInvitation();
  const [open, setOpen] = useState(false);

  return (
    <div className="space-y-4">
      <AdminsCard />

      <Card>
      <CardHeader className="flex items-start justify-between">
        <div>
          <h3 className="text-sm font-semibold">Pending invitations</h3>
          <p className="text-xs text-[var(--color-foreground-muted)]">
            Invite other admins to this app. Portal users get invited from a client's Contacts or
            Portal access tab.
          </p>
        </div>
        <Button onClick={() => setOpen(true)}>
          <Send className="size-4" /> Invite admin
        </Button>
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>
        ) : !data || data.length === 0 ? (
          <EmptyState
            icon={Mail}
            title="No pending invitations"
            description="When you invite someone, the invitation will appear here until they accept or it expires."
          />
        ) : (
          <ul className="divide-y divide-[var(--color-border)]">
            {data.map((inv) => (
              <li key={inv.id} className="flex items-center justify-between py-2.5">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium">{inv.name}</span>
                    <Badge tone="info">{formatStatus(inv.role)}</Badge>
                  </div>
                  <div className="text-xs text-[var(--color-foreground-muted)]">
                    {inv.email} · expires {formatDateLong(inv.expires_at)}
                  </div>
                </div>
                <IconButton
                  onClick={() => revoke.mutate(inv.id)}
                  aria-label="Revoke invitation"
                >
                  <X className="size-4" />
                </IconButton>
              </li>
            ))}
          </ul>
        )}
      </CardBody>

      <InviteDialog
        open={open}
        onClose={() => setOpen(false)}
        title="Invite an admin"
        role="admin"
        hint="They'll receive an email with a signed link to set their password."
      />
      </Card>
    </div>
  );
}

/**
 * Everyone who already has admin access. Access is revoked by deactivating,
 * never by deleting: an admin is named on invoices, payments, timeline entries
 * and journal entries, so removing the row would rewrite who did what.
 */
function AdminsCard() {
  const { data, isLoading } = useTeam();

  return (
    <Card>
      <CardHeader>
        <h3 className="text-sm font-semibold">Admins</h3>
        <p className="text-xs text-[var(--color-foreground-muted)]">
          Everyone who can sign in to the admin app. Revoking someone takes effect immediately,
          including on a session they already have open.
        </p>
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>
        ) : !data || data.length === 0 ? (
          <EmptyState icon={Users} title="No admins yet" description="Invite one below." />
        ) : (
          <ul className="divide-y divide-[var(--color-border)]">
            {data.map((member) => (
              <AdminRow key={member.id} member={member} />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function AdminRow({ member }: { member: TeamMemberData }) {
  const setAccess = useSetTeamMemberAccess();
  const confirm = useConfirm();
  const toast = useToast();
  const revoked = member.deactivated_at != null;

  return (
    <li className="flex items-center justify-between gap-3 py-2.5">
      <div className="flex min-w-0 items-center gap-3">
        <EntityAvatar name={member.name} src={member.avatar_thumb_url} />
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="truncate font-medium">{member.name}</span>
            {member.is_you ? <Badge tone="neutral">You</Badge> : null}
            {revoked ? <Badge tone="danger">Access revoked</Badge> : null}
            {!revoked && !member.two_factor_enabled ? (
              <Badge tone="warning">No two-factor</Badge>
            ) : null}
          </div>
          <div className="truncate text-xs text-[var(--color-foreground-muted)]">
            {member.email} · joined {formatDateLong(member.joined_at)}
          </div>
        </div>
      </div>

      {member.is_you ? null : (
        <Menu
          items={[
            revoked
              ? {
                  key: 'restore',
                  label: 'Restore access',
                  icon: RotateCcw,
                  onSelect: async () => {
                    await setAccess.mutateAsync({ id: member.id, active: true });
                    toast.success(`${member.name} can sign in again`);
                  },
                }
              : {
                  key: 'revoke',
                  label: 'Revoke access',
                  icon: Ban,
                  danger: true,
                  onSelect: async () => {
                    const ok = await confirm({
                      title: `Revoke ${member.name}'s access?`,
                      body: 'They are signed out at once and cannot sign back in. Their name stays on everything they did, which is why the account is kept rather than deleted. You can restore access later.',
                      confirmLabel: 'Revoke access',
                      tone: 'danger',
                    });
                    if (ok) {
                      await setAccess.mutateAsync({ id: member.id, active: false });
                      toast.success(`${member.name}'s access revoked`);
                    }
                  },
                },
          ]}
        />
      )}
    </li>
  );
}
